import { difference, uniqBy } from 'lodash';
import { dispatchAction } from '@ardoq/rxbeach';
import { hideRightPane } from 'appContainer/actions';
import { navigate } from 'router/declarative/streamRouterAdapter/actions';
import { isPresentationMode } from 'env';
import { formatListAsEnglishProse } from '@ardoq/common-helpers';
import { logError, logWarn } from '@ardoq/logging';
import {
  APIReferenceAttributes,
  APIPresentationAssetAttributes,
  APIWorkspaceAttributes,
  ArdoqId,
  Entity,
  Named,
} from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { toIds } from '@ardoq/core';
import {
  notifyScenarioChanged,
  notifyWorkspaceChanged,
  ResetStatePayload,
  notifyContextChanged,
} from 'streams/context/ContextActions';
import {
  loadAggregateWorkspaces,
  loadAggregateWorkspacesSucceeded,
  loadAggregateWorkspacesFailed,
} from 'sync/actions';
import { dispatchActionAndWaitForResponse } from 'actions/utils';
import { isScenarioMode } from 'models/utils/scenarioUtils';
import {
  setIsLoading,
  setIsLoadingWithConfig,
} from 'components/GlobalPaneLoader/globalPaneLoader$';
import { isSameSet } from '@ardoq/common-helpers';
import { allWorkspacesClosed } from 'streams/views/mainContent/actions';
import {
  notifyWorkspaceAggregatedLoaded,
  markWorkspaceAggregatedLoaded,
  markWorkspaceAggregatedUnloaded,
} from 'streams/workspaces/actions';
import { isWorkspaceCurrentlyLoaded } from 'streams/workspaces/aggregatedLoaded$';
import * as profiling from '@ardoq/profiling';
import { clearLoadedState } from 'loadedState/actions';

const calculateNewWorkspaceOrder = (
  workspaceOrder: ArdoqId[],
  currentWorkspaceIds: ArdoqId[]
): ArdoqId[] => {
  if (currentWorkspaceIds.length === 0) {
    return [];
  }

  return [
    ...currentWorkspaceIds,
    ...workspaceOrder.filter(id => !currentWorkspaceIds.includes(id)),
  ];
};

const isWorkspaceUninitialized = (
  workspaceId: string,
  currentWorkspaceIds: Set<string>
): boolean =>
  !currentWorkspaceIds.has(workspaceId) &&
  !isWorkspaceCurrentlyLoaded(workspaceId) &&
  !loadingWorkspaceIds.has(workspaceId);

const loadUninitializedWorkspaces = async (
  uninitializedWorkspaces: (Entity & Named)[],
  transaction: any
): Promise<void> => {
  // Calculate title and show loading UI
  const title = formatListAsEnglishProse(
    uninitializedWorkspaces.map(ws => ws.name)
  );
  dispatchAction(
    setIsLoadingWithConfig({
      title: `Loading "${title}"\u2026`,
    })
  );

  // Mark workspaces as loading
  const workspaceIds = toIds(uninitializedWorkspaces);
  workspaceIds.forEach(id => loadingWorkspaceIds.add(id));

  const result = await dispatchActionAndWaitForResponse(
    loadAggregateWorkspaces({ workspaceIds, transaction }),
    loadAggregateWorkspacesSucceeded,
    loadAggregateWorkspacesFailed
  );

  // Always clean up loading set and hide loading UI
  workspaceIds.forEach(id => loadingWorkspaceIds.delete(id));
  dispatchAction(setIsLoading(false));

  if (result.type === loadAggregateWorkspacesFailed.type) {
    const { payload } = result;
    throw payload?.error;
  }
};

export type LoadWorkspacesOpts = {
  /**
   * Additional loaded workspaces
   */
  workspaces: APIWorkspaceAttributes[];
  /**
   * The "selected" workspace
   */
  contextWorkspace?: APIWorkspaceAttributes;
  resetComponentContext?: boolean;
  workspaceOrder?: ArdoqId[];
  transaction?: profiling.Transaction;
};

export type CloseWorkspaceOpts = {
  isRegisterLoadedState?: boolean;
};

export type SetOpenWorkspacesArgs = {
  /**
   * All the workspaces which are supposed to be set or stay open.
   */
  workspaces: APIWorkspaceAttributes[];
  /**
   * A request to focus this workspace. `null`/omitted, or the workspace which
   * is already active, means "keep the current context untouched".
   */
  activeWorkspace?: APIWorkspaceAttributes | null;
  workspaceOrder?: ArdoqId[] | null;
};

const warnIfInconsistentWorkspaceState = (
  activeWorkspaceId: ArdoqId | undefined,
  currentlyActiveWorkspaceId: ArdoqId | null,
  workspaceIdsToClose: ArdoqId[],
  openWorkspaceIds: ArdoqId[]
): void => {
  if (
    currentlyActiveWorkspaceId &&
    !openWorkspaceIds.includes(currentlyActiveWorkspaceId) &&
    !workspaceIdsToClose.includes(currentlyActiveWorkspaceId)
  ) {
    logWarn(
      Error(
        'currentlyActiveWorkspace is not in opened nor closed workspaces, ' +
          'this means workspaces were closed without regard to active workspace'
      ),
      'Context: Inconsistent state',
      {
        activeWorkspaceId,
        currentlyActiveWorkspaceId,
        workspacesToClose: workspaceIdsToClose,
        openWorkspaces: openWorkspaceIds,
      }
    );
  }
};

/**
 * `null` means the focus must not move, so the context is left alone entirely -
 * including the selected component, reference and scenario. Callers which
 * merely re-declare the open set (viewpoint mode rebuilds it on every websocket
 * update) must not steal the focus (ARD-33167). A currently active workspace
 * which is about to be closed cannot be kept.
 */
const getNewActiveWorkspaceId = (
  requestedActiveWorkspaceId: ArdoqId | undefined,
  currentlyActiveWorkspaceId: ArdoqId | null,
  openWorkspaceIds: ArdoqId[]
): ArdoqId | null => {
  if (
    requestedActiveWorkspaceId &&
    requestedActiveWorkspaceId !== currentlyActiveWorkspaceId
  ) {
    return requestedActiveWorkspaceId;
  }
  if (
    currentlyActiveWorkspaceId &&
    openWorkspaceIds.includes(currentlyActiveWorkspaceId)
  ) {
    return null;
  }
  return openWorkspaceIds[0] ?? null;
};

// Track workspaces currently being loaded
const loadingWorkspaceIds = new Set<ArdoqId>();

const toNamedEntity = (ws: APIWorkspaceAttributes): Entity & Named => ({
  _id: ws._id,
  name: ws.name,
});

const safeLoadUninitializedWorkspaces = async (
  uninitializedWorkspaces: (Entity & Named)[],
  transaction: any
): Promise<void> => {
  if (uninitializedWorkspaces.length === 0) {
    return;
  }

  if (isScenarioMode()) {
    logWarn(
      new Error(
        `Blocked attempt to load ${uninitializedWorkspaces.length} extra workspace(s) when in scenario mode. The source of this trigger should be fixed.`
      )
    );
    return;
  }

  await loadUninitializedWorkspaces(uninitializedWorkspaces, transaction);
};

export class ContextBackboneModel {
  contextMessage: any;
  _componentId: ArdoqId | null;
  _referenceId: ArdoqId | null;
  _workspaceId: ArdoqId | null;
  // This is a bit a workaround, trying to follow as closely as possible the
  // existing patterns, but scenario is only set if it's the selected node
  // in the navigator, so more like the selected component, even though
  // all the workspaces are actualluy hierachically nodes of the scenario.
  _scenarioId: ArdoqId | null;
  _workspaceIds: ArdoqId[];
  _presentationId: ArdoqId | null;
  _isExploreMode: boolean;
  constructor() {
    this._componentId = null;
    this._workspaceId = null;
    this._scenarioId = null;
    this._workspaceIds = [];
    this._referenceId = null;
    this._presentationId = null;
    this._isExploreMode = false;
  }

  /**
   * IMPORTANT: Sets the open workspaces and preventing accidental "workspace
   * mode" loads. Use this method when you've manually "loaded" the workspaces
   * with the data you want, and you need Context to reflect this and NOT try
   * to load the workspaces again. Note that workspaces not in the provided
   * list are closed(!).
   *
   * Use when you have manually reset the entity collections with exactly the
   * data you want it to have. Example use cases: Viewpoint Mode, Scenarios,
   * Presentations and GridEditor.
   */
  setOpenWorkspaces({
    workspaces,
    activeWorkspace = null,
    workspaceOrder,
  }: SetOpenWorkspacesArgs) {
    // Ensure there are no duplicates.
    const openWorkspaces = uniqBy(workspaces, '_id');
    const openWorkspaceIds = toIds(openWorkspaces);
    const currentWorkspaceIds = this.workspaceIds();

    const workspaceIdsToOpen = difference(
      openWorkspaceIds,
      currentWorkspaceIds
    );
    const workspaceIdsToClose = difference(
      currentWorkspaceIds,
      openWorkspaceIds
    );

    const workspaceIdsToOpenSet = new Set(workspaceIdsToOpen);
    const workspacesToOpen = openWorkspaces.filter(ws =>
      workspaceIdsToOpenSet.has(ws._id)
    );

    workspacesToOpen.forEach(workspace => {
      const workspaceId = workspace._id;
      dispatchAction(markWorkspaceAggregatedLoaded({ workspaceId }));
      this._workspaceIds.push(workspaceId);
      dispatchAction(notifyWorkspaceAggregatedLoaded({ workspaceId }));
    });
    dispatchAction(notifyContextChanged());

    warnIfInconsistentWorkspaceState(
      activeWorkspace?._id,
      this._workspaceId,
      workspaceIdsToClose,
      openWorkspaceIds
    );

    const newActiveWorkspaceId = getNewActiveWorkspaceId(
      activeWorkspace?._id,
      this._workspaceId,
      openWorkspaceIds
    );

    if (newActiveWorkspaceId) {
      this.setContextWorkspace(newActiveWorkspaceId, true);
    }

    workspaceIdsToClose.forEach(workspaceId =>
      this.justCloseWorkspace(workspaceId)
    );

    if (workspaceOrder) {
      this.setWorkspaceOrder(workspaceOrder);
    }
  }
  // Use this only with the CANVAS_PROTOTYPE flag.
  // Workspace subscriptions are basically bound to loaded workspaces, so
  // basically managed by the Context class. In the CANVAS_PROTOTYPE the loaded
  // workspaces don't really matter much, so it's fine to set all workspaces we
  // would like to get notifications about to loaded. If the user creates e.g.
  // a new triple, we need to make sure that we subscribe to the according
  // workspace notifications before hand, so we get the according updates on the
  // websocket.
  ensureWorkspaceSubscriptionInViewpointMode(workspaceIds: ArdoqId[]) {
    const openWorkspaceIds = [...new Set(workspaceIds)];
    const currentWorkspaceIds = this.workspaceIds();
    const workspaceIdsToOpen = difference(
      openWorkspaceIds,
      currentWorkspaceIds
    );

    workspaceIdsToOpen.forEach(workspaceId => {
      dispatchAction(markWorkspaceAggregatedLoaded({ workspaceId }));
      this._workspaceIds.push(workspaceId);
    });
    dispatchAction(notifyContextChanged());
  }

  activeWorkspaceId(): string | null {
    return this._workspaceId;
  }

  componentId(): string | null {
    return this._componentId;
  }

  scenarioId() {
    return this._scenarioId;
  }

  referenceId(): string | null {
    return this._referenceId;
  }

  /** Sets the context to a loaded workspace */
  setContextWorkspace(workspaceId: ArdoqId, resetComponentContext: boolean) {
    const workspaceActuallyChanged = workspaceId !== this._workspaceId;
    const hasScenarioChanged = this._scenarioId !== null;
    this._scenarioId = null;
    this._workspaceId = workspaceId;

    if (resetComponentContext) {
      this.unsetComponent();
      this.unsetReference();
    }

    if (workspaceActuallyChanged || hasScenarioChanged) {
      dispatchAction(notifyWorkspaceChanged({ workspaceId }));
    }
  }

  private openLoadedWorkspaces(workspaceIds: ArdoqId[]) {
    const currentWorkspaceIds = new Set(this.workspaceIds());
    const workspaceIdsToOpen = difference(workspaceIds, [
      ...currentWorkspaceIds,
    ]);

    workspaceIdsToOpen.forEach(workspaceId => {
      this._workspaceIds.push(workspaceId);
    });
    dispatchAction(notifyContextChanged());
  }

  private async ensureWorkspacesLoaded(
    workspaces: APIWorkspaceAttributes[],
    transaction: any
  ): Promise<void> {
    const currentWorkspaceIds = new Set(this.workspaceIds());

    const uninitializedWorkspaces = workspaces.filter(ws =>
      isWorkspaceUninitialized(ws._id, currentWorkspaceIds)
    );

    await safeLoadUninitializedWorkspaces(
      uninitializedWorkspaces.map(toNamedEntity),
      transaction
    );
  }

  async forceReloadWorkspaces(
    workspaces: APIWorkspaceAttributes[],
    transaction: any
  ): Promise<void> {
    const currentWorkspaceIds = [...this.workspaceIds()];
    const activeWorkspaceId = this.activeWorkspaceId();

    // Important:
    // Only replacing scope data and triggering notifyContextChanged is not
    // sufficient for all modules to reload their data because they rely on
    // distinctUntilChanged or equivalent deep comparison on context$ to avoid
    // expensive calculations. To make sure context$ emits after we have
    // reloaded the workspaces we close the workspaces, notify changes, load
    // the new data, reset the workspace ids, then notify changes again.
    // In particular, the PostMessageBridge-sync with the GridEditor is one
    // such module that relies on distinctUntilChanged to avoid serializing and
    // sending large amounts of data over postMessage unnecessarily. Other
    // modules that read data via interfaces also suffer from the same problem.
    this.closeAllWorkspacesSilently();
    dispatchAction(notifyContextChanged());

    await safeLoadUninitializedWorkspaces(
      workspaces.map(toNamedEntity),
      transaction
    );

    this._workspaceIds = currentWorkspaceIds;
    this._workspaceId = activeWorkspaceId;
    dispatchAction(notifyContextChanged());
  }

  // Loads workspaces in the background (i.e. doesn't set them as context)
  async loadWorkspaces({
    workspaces = [],
    contextWorkspace,
    resetComponentContext = true,
    workspaceOrder,
    transaction = profiling.noOpTransaction,
  }: LoadWorkspacesOpts) {
    const loadWorkspacesSpan = profiling.startSpan(
      transaction,
      'load workspaces'
    );
    const wantedWorkspaces = [contextWorkspace, ...workspaces].filter(isTruthy);
    const uniqueWorkspaces = uniqBy(wantedWorkspaces, '_id');

    if (!isPresentationMode()) {
      await this.ensureWorkspacesLoaded(uniqueWorkspaces, transaction);
    }

    this.openLoadedWorkspaces(toIds(uniqueWorkspaces));

    if (contextWorkspace) {
      this.setContextWorkspace(contextWorkspace._id, resetComponentContext);
    }
    if (workspaceOrder) {
      const actualWorkspaceOrder = calculateNewWorkspaceOrder(
        workspaceOrder,
        this.workspaceIds()
      );

      this.setWorkspaceOrder(actualWorkspaceOrder);
    }

    profiling.endSpan(loadWorkspacesSpan);
  }

  async switchToWorkspace(workspace: APIWorkspaceAttributes) {
    const transaction = profiling.noOpTransaction;
    const loadWorkspacesSpan = profiling.startSpan(
      transaction,
      'switch to workspace'
    );
    if (!isPresentationMode()) {
      await this.ensureWorkspacesLoaded([workspace], transaction);
    }

    const workspaceId = workspace._id;
    if (!this.workspaceIds().includes(workspaceId)) {
      this._workspaceIds.push(workspaceId);
    }
    dispatchAction(notifyContextChanged());

    this.setContextWorkspace(workspace._id, true);

    profiling.endSpan(loadWorkspacesSpan);
  }

  async loadWorkspacesInBackground(workspaces: APIWorkspaceAttributes[]) {
    const transaction = profiling.noOpTransaction;
    const loadWorkspacesSpan = profiling.startSpan(
      transaction,
      'load workspaces in background'
    );
    const uniqueWorkspaces = uniqBy(workspaces, '_id');

    if (!isPresentationMode()) {
      await this.ensureWorkspacesLoaded(uniqueWorkspaces, transaction);
    }

    this.openLoadedWorkspaces(toIds(uniqueWorkspaces));

    profiling.endSpan(loadWorkspacesSpan);
  }

  async loadWorkspacesAndSetContext(
    contextWorkspace: APIWorkspaceAttributes,
    workspaces: APIWorkspaceAttributes[]
  ) {
    const transaction = profiling.noOpTransaction;
    const loadWorkspacesSpan = profiling.startSpan(
      transaction,
      'load workspaces and set context'
    );
    const allWorkspacesToLoad = [contextWorkspace, ...workspaces];
    const uniqueWorkspaces = uniqBy(allWorkspacesToLoad, '_id');

    if (!isPresentationMode()) {
      await this.ensureWorkspacesLoaded(uniqueWorkspaces, transaction);
    }

    this.openLoadedWorkspaces(toIds(uniqueWorkspaces));

    this.setContextWorkspace(contextWorkspace._id, true);

    profiling.endSpan(loadWorkspacesSpan);
  }

  setWorkspaceOrder(workspaceOrder: ArdoqId[]) {
    const loadedWorkspaceIds = this.workspaceIds();
    if (!isSameSet(workspaceOrder, loadedWorkspaceIds)) {
      logError(
        Error("Ids of workspace order doesn't match loaded workspaces"),
        null,
        { workspaceOrder, loadedWorkspaceIds }
      );
      return;
    }

    if (loadedWorkspaceIds.every((id, index) => id === workspaceOrder[index])) {
      return;
    }

    this._workspaceIds = workspaceOrder;
    dispatchAction(notifyContextChanged());
  }

  isExploreMode() {
    return this._isExploreMode;
  }
  toggleExploreMode(isExploreMode: boolean) {
    this._isExploreMode = isExploreMode;
  }
  /**
   * This method is used when closing all workspaces and resetting the collections
   * manually. An event to notify the views must also be triggered.
   *
   */
  closeAllWorkspacesSilently() {
    this.workspaceIds().forEach(workspaceId => {
      dispatchAction(markWorkspaceAggregatedUnloaded({ workspaceId }));
    });
    this._workspaceIds = [];
    this._workspaceId = null;
  }
  justCloseWorkspace(workspaceId: ArdoqId) {
    this._workspaceIds = this._workspaceIds.filter(id => id !== workspaceId);
    dispatchAction(markWorkspaceAggregatedUnloaded({ workspaceId }));
    dispatchAction(notifyContextChanged());
  }

  closeWorkspace(
    workspaceId: ArdoqId,
    { isRegisterLoadedState = true }: CloseWorkspaceOpts = {}
  ) {
    const moreThanOneWorkspaceLoaded = this._workspaceIds.length > 1;

    if (moreThanOneWorkspaceLoaded) {
      const isClosingActiveWs = workspaceId === this._workspaceId;
      this.justCloseWorkspace(workspaceId);

      const workspaceIds = this.workspaceIds();
      const newWSId = isClosingActiveWs
        ? workspaceIds[workspaceIds.length - 1]
        : this._workspaceId;
      if (this._workspaceId === newWSId) {
        // The context hasn't really changed, but since the old event
        // was triggering a workspace change when a workspace
        // was unloaded, we need to keep this around
        const workspaceId = newWSId;
        dispatchAction(notifyWorkspaceChanged({ workspaceId }));
      } else if (newWSId) {
        this.setContextWorkspace(newWSId, true);
      }
    } else {
      this.closeAllWorkspaces({ isRegisterLoadedState });
    }
  }

  /**
   * Used to reset the state before we load a search or traversal slide.
   *
   * When we load a traversal slide we close all workspaces to reset the
   * collections because a traversal loads only a subset of all the
   * workspace entities (component or references). This is needed because for
   * a slide loaded with workspace loading we have an optimization to not reload
   * all workspaces, but only the ones which are new compared to the previous
   * slide, keeping all loaded workspace entities of these workspaces in the
   * according collections.
   */

  resetState(payload: ResetStatePayload) {
    this.closeAllWorkspacesSilently();
    const transaction = profiling.startTransaction(
      'resetState',
      500,
      profiling.Team.CORE
    );

    const workspaceActuallyChanged = this._workspaceId !== null;
    const hasScenarioChanged = this._scenarioId !== null;
    this._componentId = null;
    this._referenceId = null;
    this._scenarioId = null;
    this._workspaceId = null;

    if (workspaceActuallyChanged) {
      dispatchAction(notifyWorkspaceChanged({ workspaceId: null }));
    } else if (hasScenarioChanged) {
      dispatchAction(notifyWorkspaceChanged({ workspaceId: null }));
    }

    profiling.endTransaction(transaction, {
      metadata: {
        hasScenarioChanged,
        resetComponentContext: true,
        wasContextWorkspaceLoaded: false,
        workspaceActuallyChanged,
      },
    });
    dispatchAction(notifyContextChanged());
    dispatchAction(allWorkspacesClosed(payload));
  }

  closeAllWorkspaces({
    isRegisterLoadedState = true,
  }: CloseWorkspaceOpts = {}) {
    this.resetState({ keepCurrentViewPointMode: false });

    if (!isPresentationMode()) {
      this.setPresentation(null);
    }

    dispatchAction(navigate({ route: '/home', params: {} }));
    dispatchAction(hideRightPane());

    if (isRegisterLoadedState) {
      dispatchAction(clearLoadedState());
    }
  }

  workspaceIds() {
    return structuredClone(this._workspaceIds);
  }

  unsetComponent() {
    if (this._componentId === null && this._referenceId === null) {
      return;
    }
    this.unsetReference(true);
    this._scenarioId = null;
    this._componentId = null;
    const workspaceId = this.activeWorkspaceId() ?? null;
    dispatchAction(notifyWorkspaceChanged({ workspaceId }));
  }

  unsetComponentId() {
    if (this._componentId === null) {
      return;
    }

    this._componentId = null;
    dispatchAction(notifyContextChanged());
  }

  setComponentUnsafe(componentId: ArdoqId) {
    if (this._componentId === componentId && this._referenceId === null) {
      return;
    }

    this.unsetReference(true);
    this._scenarioId = null;
    this._componentId = componentId;
    dispatchAction(notifyContextChanged());
  }

  setScenarioId(scenarioId: ArdoqId | null) {
    if (this._scenarioId !== scenarioId) {
      this.unsetComponent();
      this._scenarioId = scenarioId;
      dispatchAction(notifyScenarioChanged(scenarioId));
    }
  }

  setReference(ref: APIReferenceAttributes, skipEvent?: boolean) {
    const newReferenceId = ref._id;
    if (this._referenceId !== newReferenceId) {
      if (this._componentId !== ref.target) {
        this._componentId = ref.source;
        this._workspaceId = ref.rootWorkspace;
      }

      this._referenceId = newReferenceId;
      if (!skipEvent) {
        dispatchAction(notifyContextChanged());
      }
    }
  }

  unsetReference(skipEvent?: boolean) {
    if (this._referenceId === null) {
      return;
    }

    this._referenceId = null;
    if (!skipEvent) {
      dispatchAction(notifyContextChanged());
    }
  }

  setPresentation(presentation: APIPresentationAssetAttributes | null) {
    if (this._presentationId !== presentation?._id) {
      this._presentationId = presentation?._id ?? null;
      dispatchAction(notifyContextChanged());
    }
  }

  presentationId() {
    return this._presentationId;
  }
}

export const context = new ContextBackboneModel();
